import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '@/utils/api';
import { Search, X, Loader2, Building2, Clock } from 'lucide-react';

/**
 * Searchable Project Picker for mobile-friendly project selection
 * - Debounced search (300ms)
 * - Shows recent projects when focused without typing
 * - Minimum 2 characters to search
 */
const ProjectSearchPicker = ({ 
  value, // { project_id, project_name }
  onChange, // (project) => void
  userCity,
  placeholder = "Search project name...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Search projects when debounced query changes
  useEffect(() => {
    const searchProjects = async () => {
      if (debouncedQuery.length < 2) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const response = await api.searchProjects(debouncedQuery, userCity, 20);
        setResults(response.data?.projects || []);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };
    
    searchProjects();
  }, [debouncedQuery, userCity]);

  // Load recent projects when focused
  const loadRecentProjects = useCallback(async () => {
    setLoadingRecent(true);
    try {
      // Try to get recent projects, fallback to top active projects
      const response = await api.getActiveProjects({ city: userCity, limit: 5 });
      setRecentProjects(response.data?.projects?.slice(0, 5) || []);
    } catch (error) {
      console.error('Failed to load recent projects:', error);
    } finally {
      setLoadingRecent(false);
    }
  }, [userCity]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFocus = () => {
    setIsOpen(true);
    if (recentProjects.length === 0) {
      loadRecentProjects();
    }
  };

  const handleSelect = (project) => {
    onChange({
      project_id: project.project_id,
      project_name: project.project_name || project.name
    });
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearchQuery('');
    inputRef.current?.focus();
  };

  // Determine what to show
  const showResults = debouncedQuery.length >= 2;
  const displayItems = showResults ? results : recentProjects;
  const isLoading = showResults ? loading : loadingRecent;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input Field */}
      <div 
        className={`relative flex items-center border rounded-xl bg-white transition-colors
          ${isOpen ? 'border-[#EF4444] ring-2 ring-red-100' : 'border-slate-200 hover:border-slate-300'}`}
      >
        <Search size={16} className="absolute left-3 text-slate-400" />
        
        {value?.project_id ? (
          // Selected state
          <div className="flex-1 flex items-center justify-between pl-9 pr-2 py-2.5">
            <span className="text-sm text-slate-800 truncate">{value.project_name}</span>
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X size={16} className="text-slate-500" />
            </button>
          </div>
        ) : (
          // Search input state
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleFocus}
            placeholder={placeholder}
            className="flex-1 pl-9 pr-3 py-2.5 text-sm bg-transparent outline-none rounded-xl"
          />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !value?.project_id && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          {!showResults && (
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <Clock size={12} />
                <span>Recent Projects</span>
              </div>
            </div>
          )}
          
          {showResults && searchQuery.length >= 2 && (
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
              <span className="text-xs text-slate-500">
                {loading ? 'Searching...' : `${results.length} result${results.length !== 1 ? 's' : ''}`}
              </span>
            </div>
          )}

          {/* Results List */}
          <div className="max-h-[50vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-slate-400" />
              </div>
            ) : searchQuery.length > 0 && searchQuery.length < 2 ? (
              <div className="py-6 text-center">
                <p className="text-sm text-slate-500">Type at least 2 characters</p>
              </div>
            ) : displayItems.length === 0 ? (
              <div className="py-6 text-center">
                <Building2 size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">
                  {showResults ? 'No projects found' : 'No recent projects'}
                </p>
              </div>
            ) : (
              displayItems.map((project, idx) => (
                <button
                  key={project.project_id}
                  type="button"
                  onClick={() => handleSelect(project)}
                  className={`w-full px-3 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-center gap-3
                    ${idx !== displayItems.length - 1 ? 'border-b border-slate-50' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {project.project_name || project.name}
                    </p>
                    {project.city && (
                      <p className="text-xs text-slate-500">{project.location ? `${project.location}, ` : ''}{project.city}</p>
                    )}
                  </div>
                  {project.status === 'Active' && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 rounded">
                      Active
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Close button for mobile */}
          <div className="sticky bottom-0 p-2 bg-white border-t border-slate-100">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSearchPicker;
